// ISC License
// Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2023 as part of Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2025.
// Permission to use, copy, modify, and/or distribute this software for any purpose with or without fee is hereby granted, provided that the above copyright notice and this permission notice appear in all copies.
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.

import { useState, useEffect, useRef } from 'react';
import Section from './Section';
import { Briefcase, Scale, Shield, Gavel, Handshake, Landmark } from 'lucide-react';

const serviceItems = [
    {
        icon: Scale,
        title: "Prawo Cywilne",
        description: "Zapewniam kompleksową obsługę spraw cywilnych, od sporządzania i analizowania umów, przez reprezentację w sporach sądowych, po bieżące doradztwo prawne. Reprezentuję Klientów w postępowaniach o zapłatę, odszkodowanie i zadośćuczynienie, w sprawach dotyczących współwłasności, roszczeń z tytułu rękojmi i gwarancji, jak również z zakresu roszczeń wynikających z umów kredytowych."
    },
    {
        icon: Briefcase,
        title: "Prawo Gospodarcze i Handlowe",
        description: "Wspieram przedsiębiorców na każdym etapie prowadzenia działalności gospodarczej, od jej założenia, przez bieżącą obsługę prawną, aż po rozwiązywanie sporów."
    },
    {
        icon: Gavel,
        title: "Prawo Rodzinne i Spadkowe",
        description: "Udzielam pomocy prawnej w sprawach rozwodowych, alimentacyjnych oraz dotyczących władzy rodzicielskiej i kontaktów z dziećmi. Prowadzę również postępowania związane z podziałem majątku wspólnego, rozdzielnością majątkową małżeńską, ubezwłasnowolnieniem, jak również w zakresie ustalenia i zaprzeczenia ojcostwa."
    },
    {
        icon: Shield,
        title: "Prawo Spadkowe",
        description: "Pomagam w sprawach związanych z dziedziczeniem, sporami spadkowymi i zarządzaniem majątkiem spadkowym. Reprezentuję Klientów w postępowaniach o stwierdzenie nabycia spadku, zachowek, uznanie za niegodnego dziedziczenia oraz dział spadku."
    },
    {
        icon: Landmark,
        title: "Prawo pracy i ubezpieczeń społecznych ",
        description: "Oferuję reprezentację i doradztwo w sporach pracowniczych, w szczególności w sprawach dotyczących ustania stosunku pracy, o zapłatę wynagrodzeń, mobbingu czy też odpowiedzialności pracowników. Wspieram również w postępowaniach przeciwko Zakładowi Ubezpieczeń Społecznych."
    },
];

const Services = ({ id }) => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    const ServiceCard = ({ icon: Icon, title, description, delay }) => (
        <div
            className={`
                bg-white p-6 md:p-8 rounded-xl shadow-lg
                hover:shadow-2xl transition-all duration-300 ease-in-out
                transform hover:-translate-y-1 border border-gray-100
                ${isVisible ? 'animate-fadeInUp' : 'opacity-0 translate-y-8'}
            `}
            style={{ animationDelay: `${delay * 150}ms`, animationDuration: '0.8s' }}
        >
            <div className="flex items-center space-x-4 mb-4">
                <Icon className="w-8 h-8 text-[#1B5125]" aria-hidden="true" />
                <h3 className="text-xl font-semibold text-[#1B5125]">{title}</h3>
            </div>
            <p className="text-gray-600 leading-relaxed">{description}</p>
        </div>
    );

    return (
        <Section id={id} className="bg-gray-50" ref={sectionRef}>
            <style>
                {`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .animate-fadeInUp {
                        animation-name: fadeInUp;
                        animation-fill-mode: both;
                    }
                `}
            </style>
            <div className="text-left mb-12">
                <h2 className="text-4xl font-extrabold text-[#1B5125] sm:text-5xl tracking-tight">
                    Zakres Usług
                </h2>
                <p className="mt-4 text-xl text-gray-500 max-w-2xl">
                    Kompleksowe doradztwo prawne i reprezentacja w kluczowych obszarach prawa.
                </p>
            </div>

            <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                ref={sectionRef}
            >
                {serviceItems.map((item, index) => (
                    <ServiceCard
                        key={index}
                        icon={item.icon}
                        title={item.title}
                        description={item.description}
                        delay={index}
                    />
                ))}
            </div>
        </Section>
    );
};

export default Services;
